import React from 'react'
import { Box, Typography } from '@mui/material'
import { SUMMARY_PALETTE } from './summary.palette'
import CustomAvatar from '../../../avatar/Avatar'

// MF-526 — Student identity header for the summary section. Deliberately SEPARATE from the metric
// cards: it carries who the student is (photo + name + year + house/form group) and reserves a
// right-hand `actions` slot for the future goal-setting controls (not built yet). Keeping it out of
// SummaryGrid means those actions can land later without touching the reusable cards.
//
// student: { name, role, yearLevel, house, formGroup, photo } — any field may be null (the line
// is simply omitted). Renders nothing if there's no student.
const StudentSummaryHeader = ({ student, actions = null }) => {
  if (!student) return null

  const { name, role = 'Student', yearLevel, house, formGroup, photo } = student
  // MF-542 — reuse the app's CustomAvatar (the same one the nav/header uses) so the photo resolves
  // through the shared `${storage}/tenants/<tenantId>/photos/<file>` convention: it reads the tenant
  // id itself, pre-checks the image exists, and falls back to the initials — no hand-built URL here.
  const photoSrc = photo ? `photos/${photo}` : undefined

  // "11" → "Year 11 Student"; "Year 11" → "Year 11 Student" (don't double-prefix).
  const yearText = yearLevel
    ? `${/year/i.test(String(yearLevel)) ? yearLevel : `Year ${yearLevel}`} ${role}`
    : role

  const meta = [house ? `House: ${house}` : null, formGroup ? `Form Group: ${formGroup}` : null]
    .filter(Boolean)
    .join('  ·  ')

  const initials = String(name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('')

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        pb: 2,
        borderBottom: `1px solid ${SUMMARY_PALETTE.cardBorder}`,
        minWidth: 0
      }}
    >
      <CustomAvatar
        // `photo` is a filename/key from the SIS; CustomAvatar serves it from the tenant file store
        // and falls back to the initials on 404/empty.
        variant='circular'
        size='xmedium'
        src={photoSrc}
        alt={initials}
        showCompleteAlt
        style={{
          backgroundColor: SUMMARY_PALETTE.grid,
          color: SUMMARY_PALETTE.muted,
          fontWeight: 700
        }}
      />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 26,
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: '-.01em',
            color: SUMMARY_PALETTE.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {name}
        </Typography>
        <Typography sx={{ fontSize: 14, color: SUMMARY_PALETTE.muted, mt: 0.25 }}>{yearText}</Typography>
        {meta ? (
          <Typography sx={{ fontSize: 12.5, color: SUMMARY_PALETTE.faint, mt: 0.25 }}>{meta}</Typography>
        ) : null}
      </Box>

      {/* Reserved for the future goal-setting actions (right of the name). Empty for now. */}
      {actions ? <Box sx={{ flexShrink: 0, ml: 'auto' }}>{actions}</Box> : null}
    </Box>
  )
}

export default StudentSummaryHeader
